import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, CheckCircle2, ChevronLeft, ClipboardCheck, FileText, Loader2, MapPin, Mic, Receipt, ShieldCheck, Upload, Users, X } from "lucide-react";
import { toast } from "sonner";

const TOKEN_KEY = "ellebierre_field_token";
const COMPANY_DOC_CATEGORIES = [["certificazioni","Certificazioni"],["amministrazione","Amministrazione"],["societa_legale","Azienda / legale"],["personale","Personale"],["fornitori","Fornitori"],["altro","Altro"]];
const SITE_DOC_CATEGORIES = ["Offerta e contratto","Progetto ed elaborati","Sicurezza","Contabilità","Corrispondenza","Foto","Rapportini e giornale","Certificazioni e collaudi","Fornitori e ordini","Altro"];
const ACTIONS = {
  rapportino: { label: "Rapportino", icon: ClipboardCheck, hint: "Foto, testo o dettatura del lavoro svolto", tone: "bg-orange-600" },
  ddt: { label: "DDT", icon: FileText, hint: "Fotografa o allega il documento di trasporto", tone: "bg-blue-600" },
  fattura: { label: "Fattura", icon: Receipt, hint: "Fotografa o allega la fattura", tone: "bg-violet-600" },
  presenze: { label: "Presenze", icon: Users, hint: "Detta i nomi/ore o allega il foglio presenze", tone: "bg-emerald-600" },
  task: { label: "Task", icon: ShieldCheck, hint: "Crea un'attività operativa per il cantiere", tone: "bg-slate-800" },
  documenti: { label: "Documenti", icon: Upload, hint: "Carica documenti direttamente nell'archivio del cantiere", tone: "bg-cyan-700" },
};

async function fieldFetch(path, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = "Bearer " + token;
  const res = await fetch("/api/field" + path, { ...options, headers });
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) throw new Error(data?.detail || "Operazione non riuscita");
  return data;
}

function useSpeech(setText) {
  const ref = useRef(null);
  const [listening, setListening] = useState(false);
  const supported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const toggle = () => {
    if (!supported) {
      toast.info("Dettatura non disponibile in questo browser");
      return;
    }
    if (listening && ref.current) {
      ref.current.stop();
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = "it-IT";
    r.interimResults = false;
    r.continuous = true;
    r.onresult = (e) => {
      const t = Array.from(e.results).slice(e.resultIndex).map(x => x[0].transcript).join(" ");
      if (t) setText(prev => (prev ? prev + " " : "") + t);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    ref.current = r;
    setListening(true);
    r.start();
  };
  return { supported, listening, toggle };
}

function Login({ onLogin }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (code.length < 4) return;
    setBusy(true);
    try {
      const r = await fieldFetch("/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      localStorage.setItem(TOKEN_KEY, r.token);
      onLogin(r.user);
    } catch (e2) {
      toast.error(e2.message);
      setCode("");
    } finally { setBusy(false); }
  };
  return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-5">
    <form onSubmit={submit} className="w-full max-w-md bg-white text-slate-900 rounded-[2rem] p-7 shadow-2xl">
      <div className="text-center mb-7">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-orange-600 text-white flex items-center justify-center font-black text-2xl">LB</div>
        <h1 className="text-3xl font-extrabold mt-4">Ellebierre Field</h1>
        <p className="text-slate-500 mt-2">Inserisci il tuo codice personale</p>
      </div>
      <input inputMode="numeric" pattern="[0-9]*" autoFocus value={code}
        onChange={(e)=>setCode(e.target.value.replace(/\D/g,"").slice(0,12))}
        className="w-full text-center text-4xl tracking-[0.35em] font-bold border-2 border-slate-200 focus:border-orange-500 rounded-2xl px-4 py-5 outline-none"
        placeholder="••••••" />
      <button disabled={busy || code.length < 4} className="mt-5 w-full rounded-2xl bg-orange-600 text-white text-xl font-bold py-4 disabled:opacity-40">
        {busy ? <Loader2 className="w-6 h-6 animate-spin mx-auto"/> : "Entra"}
      </button>
    </form>
  </div>;
}

function Intake({ user, action, onBack, onDone }) {
  const meta = ACTIONS[action];
  const [siteId, setSiteId] = useState(user.sites.length === 1 ? user.sites[0].id : "");
  const [text, setText] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [documentScope, setDocumentScope] = useState("cantiere");
  const [documentCategory, setDocumentCategory] = useState("Altro");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState({});
  const [workersBusy, setWorkersBusy] = useState(false);
  const [geo, setGeo] = useState({ status: "idle", latitude: null, longitude: null, accuracy: null });
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const speech = useSpeech(setText);

  const selectedSite = useMemo(() => user.sites.find(x => x.id === siteId), [user.sites, siteId]);

  useEffect(() => {
    if (!["presenze", "rapportino"].includes(action) || !siteId) {
      setWorkers([]);
      setSelectedWorkers({});
      return;
    }
    let alive = true;
    setWorkersBusy(true);
    fieldFetch("/sites/"+siteId+"/workers")
      .then(rows => {
        if (!alive) return;
        setWorkers(rows || []);
        const initial = {};
        (rows || []).forEach(w => {
          if (w.today) initial[w.id] = { hours: Number(w.today.hours ?? w.daily_hours ?? 8) };
        });
        setSelectedWorkers(initial);
      })
      .catch(e => toast.error(e.message))
      .finally(() => { if (alive) setWorkersBusy(false); });
    return () => { alive = false; };
  }, [action, siteId]);

  useEffect(() => {
    if (!navigator.geolocation) { setGeo(g => ({...g,status:"unsupported"})); return; }
    setGeo(g => ({...g,status:"loading"}));
    navigator.geolocation.getCurrentPosition(
      p => setGeo({status:"ok",latitude:p.coords.latitude,longitude:p.coords.longitude,accuracy:p.coords.accuracy}),
      () => setGeo(g => ({...g,status:"denied"})),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  }, []);

  const addFiles = (e) => {
    const xs = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...xs].slice(0,20));
    e.target.value = "";
  };

  const submit = async () => {
    if (!(action === "documenti" && documentScope === "societa") && !siteId) { toast.error("Seleziona il cantiere"); return; }
    if (action === "documenti" && files.length === 0) { toast.error("Allega almeno un documento"); return; }
    if (action !== "presenze" && action !== "documenti" && !text.trim() && files.length === 0) { toast.error("Inserisci un testo, detta oppure allega almeno un file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("action", action);
      fd.append("site_id", (action === "documenti" && documentScope === "societa") ? "" : siteId);
      fd.append("text", text.trim());
      if (action === "documenti") { fd.append("document_scope", documentScope); fd.append("document_category", documentCategory); }
      if (action === "task" && dueDate) fd.append("due_date", dueDate);
      if (geo.latitude != null) fd.append("latitude", String(geo.latitude));
      if (geo.longitude != null) fd.append("longitude", String(geo.longitude));
      if (geo.accuracy != null) fd.append("accuracy_m", String(geo.accuracy));
      files.forEach(f => fd.append("files", f));
      if (action === "presenze" || action === "rapportino") {
        const entries = workers
          .filter(w => selectedWorkers[w.id])
          .map(w => ({ worker_id: w.id, name: w.name, hours: Number(selectedWorkers[w.id]?.hours ?? w.daily_hours ?? 8), note: "" }));
        if ((action === "presenze" || action === "rapportino") && !entries.length) {
          toast.error("Seleziona almeno un operaio presente e le ore");
          setBusy(false);
          return;
        }
        if (entries.length) fd.append("attendance_json", JSON.stringify({ date: new Date().toISOString().slice(0,10), entries }));
      }
      const r = await fieldFetch("/submit", { method:"POST", body:fd });
      onDone({ ...r, site: selectedSite?.name || "" });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return <div className="min-h-screen bg-slate-100 p-3 sm:p-6">
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="w-12 h-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center"><ChevronLeft/></button>
        <div className={"w-12 h-12 rounded-2xl text-white flex items-center justify-center "+meta.tone}><meta.icon className="w-6 h-6"/></div>
        <div><div className="text-2xl font-black">{meta.label}</div><div className="text-sm text-slate-500">{meta.hint}</div></div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-4 sm:p-6 space-y-5">
        {action === "documenti" ? <>
          <div><label className="block text-sm font-bold mb-2">1. Destinazione</label><select value={documentScope} onChange={e=>{setDocumentScope(e.target.value);setDocumentCategory(e.target.value==="societa"?"altro":"Altro")}} className="w-full text-lg font-semibold border-2 border-slate-200 rounded-2xl px-4 py-4 bg-white"><option value="cantiere">Cantiere</option><option value="societa">Società</option></select></div>
          {documentScope === "cantiere" && <div><label className="block text-sm font-bold mb-2">2. Cantiere</label><select value={siteId} onChange={e=>setSiteId(e.target.value)} className="w-full text-lg font-semibold border-2 border-slate-200 rounded-2xl px-4 py-4 bg-white"><option value="">Seleziona cantiere…</option>{user.sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</select></div>}
          <div><label className="block text-sm font-bold mb-2">{documentScope === "cantiere" ? "3. Categoria" : "2. Categoria"}</label><select value={documentCategory} onChange={e=>setDocumentCategory(e.target.value)} className="w-full text-lg font-semibold border-2 border-slate-200 rounded-2xl px-4 py-4 bg-white">{(documentScope === "societa" ? COMPANY_DOC_CATEGORIES : SITE_DOC_CATEGORIES.map(x=>[x,x])).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
        </> : <div>
          <label className="block text-sm font-bold mb-2">1. Cantiere</label>
          <select value={siteId} onChange={e=>setSiteId(e.target.value)} className="w-full text-lg font-semibold border-2 border-slate-200 rounded-2xl px-4 py-4 bg-white">
            <option value="">Seleziona cantiere…</option>
            {user.sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>}

        {(action === "presenze" || action === "rapportino") && <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-bold">{action === "rapportino" ? "2. Presenze operai nel rapportino" : "2. Operai presenti"}</label>
            {!!workers.length && <button type="button" onClick={()=>{
              const all={}; workers.forEach(w=>{all[w.id]={hours:Number(w.daily_hours||8)};}); setSelectedWorkers(all);
            }} className="text-sm font-bold text-emerald-700">Seleziona tutti</button>}
          </div>
          {workersBusy ? <div className="rounded-2xl bg-slate-50 p-5 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-600"/></div> :
          <div className="space-y-3">
            {workers.map(w=>{
              const checked=!!selectedWorkers[w.id];
              return <div key={w.id} className={"rounded-2xl border-2 p-4 transition "+(checked?"border-emerald-500 bg-emerald-50":"border-slate-200 bg-white")}>
                <div className="flex items-center gap-3">
                  <input type="checkbox" className="w-7 h-7 accent-emerald-600" checked={checked} onChange={(e)=>{
                    setSelectedWorkers(prev=>{
                      const next={...prev};
                      if(e.target.checked) next[w.id]={hours:Number(w.today?.hours ?? w.daily_hours ?? 8)};
                      else delete next[w.id];
                      return next;
                    });
                  }}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-lg font-black text-slate-900 truncate">{w.name}</div>
                    <div className="text-sm text-slate-500">{w.role || "Operaio"}</div>
                  </div>
                  {checked && <div className="w-24">
                    <label className="text-[11px] font-bold text-slate-500">Ore</label>
                    <input type="number" min="0" max="24" step="0.5" value={selectedWorkers[w.id]?.hours ?? w.daily_hours ?? 8}
                      onChange={(e)=>setSelectedWorkers(prev=>({...prev,[w.id]:{...(prev[w.id]||{}),hours:e.target.value}}))}
                      className="w-full border-2 border-slate-200 rounded-xl px-2 py-2 text-center text-lg font-bold"/>
                  </div>}
                </div>
              </div>;
            })}
            {!workers.length && !workersBusy && <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">Nessun operaio attivo in anagrafica.</div>}
          </div>}
        </div>}

        {action !== "presenze" && <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-bold">2. Scrivi o detta</label>
            <button type="button" onClick={speech.toggle} className={"inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold "+(speech.listening?"bg-red-600 text-white":"bg-slate-100 text-slate-700")}>
              <Mic className="w-5 h-5"/>{speech.listening ? "Stop" : "Detta"}
            </button>
          </div>
          <textarea value={text} onChange={e=>setText(e.target.value)} rows={5}
            placeholder={action==="presenze" ? "Es. Rossi 8 ore, Bianchi mezza giornata…" : action==="task" ? "Descrivi cosa bisogna fare…" : action==="documenti" ? "Nota facoltativa per identificare i documenti…" : "Descrivi il contenuto o aggiungi una nota…"}
            className="w-full border-2 border-slate-200 rounded-2xl p-4 text-lg outline-none focus:border-orange-500"/>
        </div>}

        {action === "task" && <div>
          <label className="block text-sm font-bold mb-2">3. Scadenza</label>
          <input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)}
            className="w-full text-lg font-semibold border-2 border-slate-200 rounded-2xl px-4 py-4 bg-white outline-none focus:border-slate-500"/>
          <div className="text-xs text-slate-500 mt-1">La task comparirà anche nel giorno di scadenza nell'Agenda del cantiere.</div>
        </div>}

        {action !== "presenze" && <div>
          <label className="block text-sm font-bold mb-2">{action==="task" ? "4. Foto / allegati" : "3. Foto / allegati"}</label>
          <input ref={inputRef} type="file" multiple className="hidden" onChange={addFiles}/>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addFiles}/>
          <div className={action==="documenti" ? "grid grid-cols-1 gap-3" : "grid grid-cols-2 gap-3"}>
            {action!=="documenti" && <button onClick={()=>cameraRef.current?.click()} className="min-h-24 rounded-2xl border-2 border-dashed border-orange-300 bg-orange-50 text-orange-700 font-bold flex flex-col items-center justify-center gap-2"><Camera className="w-7 h-7"/>Scatta foto</button>}
            <button onClick={()=>inputRef.current?.click()} className="min-h-24 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 text-slate-700 font-bold flex flex-col items-center justify-center gap-2"><Upload className="w-7 h-7"/>{action==="documenti" ? "Carica documenti" : "Scegli file"}</button>
          </div>
          {!!files.length && <div className="mt-3 space-y-2">
            {files.map((f,i)=><div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 text-sm"><span className="truncate flex-1">{f.name}</span><button onClick={()=>setFiles(files.filter((_,j)=>j!==i))}><X className="w-4 h-4"/></button></div>)}
          </div>}
        </div>}

        <div className="rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3 flex items-center gap-3">
          <MapPin className={"w-5 h-5 "+(geo.status==="ok"?"text-emerald-600":"text-slate-400")}/>
          <div className="text-sm">
            <div className="font-bold">Posizione</div>
            <div className="text-slate-500">{geo.status==="ok" ? "Acquisita · precisione circa "+Math.round(geo.accuracy||0)+" m" : geo.status==="loading" ? "Acquisizione…" : geo.status==="denied" ? "Permesso posizione non concesso" : "Posizione non disponibile"}</div>
          </div>
        </div>

        <button onClick={submit} disabled={busy || (!(action === "documenti" && documentScope === "societa") && !siteId)} className={"w-full py-5 rounded-2xl text-white text-xl font-black disabled:opacity-40 "+meta.tone}>
          {busy
            ? <span className="inline-flex items-center gap-2"><Loader2 className="w-6 h-6 animate-spin"/>{["ddt","fattura"].includes(action) ? "Analisi e salvataggio…" : "Salvataggio…"}</span>
            : (["ddt","fattura"].includes(action) ? "Analizza e registra" : "Registra")}
        </button>
      </div>
    </div>
  </div>;
}

export default function Field() {
  const [user, setUser] = useState(null);

  useEffect(()=>{
    document.title = "Ellebierre Field";
    const ensureLink=(rel,href,sizes)=>{
      let el=document.querySelector('link[rel="'+rel+'"]');
      if(!el){el=document.createElement("link");el.rel=rel;document.head.appendChild(el);}
      el.href=href; if(sizes) el.sizes=sizes;
    };
    ensureLink("manifest","/field.webmanifest");
    ensureLink("apple-touch-icon","/apple-touch-icon.png","180x180");
    let meta=document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if(!meta){meta=document.createElement("meta");meta.name="apple-mobile-web-app-title";document.head.appendChild(meta);}
    meta.content="Ellebierre Field";
    if("serviceWorker" in navigator) navigator.serviceWorker.register("/service-worker.js").catch(()=>{});
  },[]);
  const [action, setAction] = useState(null);
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));

  useEffect(()=>{
    const token=localStorage.getItem(TOKEN_KEY);
    if(!token){setLoading(false);return;}
    fieldFetch("/me").then(setUser).catch(()=>localStorage.removeItem(TOKEN_KEY)).finally(()=>setLoading(false));
  },[]);

  const logout=()=>{localStorage.removeItem(TOKEN_KEY);setUser(null);setAction(null);setDone(null);};

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-9 h-9 text-orange-500 animate-spin"/></div>;
  if (!user) return <Login onLogin={setUser}/>;

  if (done) return <div className="min-h-screen bg-emerald-50 flex items-center justify-center p-5">
    <div className="w-full max-w-lg bg-white rounded-[2rem] p-7 text-center shadow-xl">
      <CheckCircle2 className="w-20 h-20 text-emerald-600 mx-auto"/>
      <h2 className="text-3xl font-black mt-4">Registrato</h2>
      <p className="text-slate-500 mt-2">{ACTIONS[done.action]?.label} · {done.site}</p>
      <button onClick={()=>{setDone(null);setAction(null)}} className="w-full mt-6 py-4 rounded-2xl bg-slate-900 text-white text-lg font-bold">Nuovo inserimento</button>
    </div>
  </div>;

  if (action) return <Intake user={user} action={action} onBack={()=>setAction(null)} onDone={setDone}/>;

  return <div className="min-h-screen bg-slate-100 p-4 sm:p-7">
    <div className="max-w-5xl mx-auto">
      <div className="bg-slate-950 text-white rounded-3xl p-5 sm:p-7 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-orange-600 flex items-center justify-center font-black text-xl">LB</div>
        <div className="flex-1"><div className="text-sm text-slate-400">Ellebierre Field</div><div className="text-2xl font-black">{user.name}</div><div className="text-sm text-slate-400">{user.sites.length} cantier{user.sites.length===1?"e":"i"} assegnati</div></div>
        <button onClick={logout} className="text-sm font-bold text-slate-300">Esci</button>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        {user.actions.map(k=>{
          const m=ACTIONS[k]; if(!m)return null; const Icon=m.icon;
          return <button key={k} onClick={()=>setAction(k)} className="bg-white border border-slate-200 rounded-3xl p-6 min-h-44 text-left shadow-sm active:scale-[0.99]">
            <div className={"w-14 h-14 rounded-2xl text-white flex items-center justify-center "+m.tone}><Icon className="w-7 h-7"/></div>
            <div className="text-2xl font-black mt-5">{m.label}</div>
            <div className="text-slate-500 mt-1">{m.hint}</div>
          </button>
        })}
      </div>
    </div>
  </div>;
}
